import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  NgZone,
  Renderer2,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'cdba-split-area',
  imports: [MatIcon, MatButtonModule, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './split-area.component.html',
})
export class SplitAreaComponent implements AfterViewInit {
  private readonly renderer = inject(Renderer2);
  private readonly ngZone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  loadingTemplate = input<TemplateRef<unknown>>();
  leftAreaTemplate = input<TemplateRef<unknown>>();
  rightAreaTemplate = input<TemplateRef<unknown>>();
  isLoading = input<boolean>(false);
  minLeftAreaWidthPercent = input<number>();
  maxLeftAreaWidthPercent = input<number>();

  @ViewChild('wrapper') wrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('leftArea') leftArea!: ElementRef<HTMLDivElement>;
  @ViewChild('separator')
  separator!: ElementRef<HTMLDivElement>;

  isDragDisabled = signal(true);

  isDragging = false;
  defaultLeftAreaWidth = 0;
  unlistenMouseMove?: () => void;
  unlistenMouseUp?: () => void;

  ngAfterViewInit(): void {
    if (!this.isLoading()) {
      this.initLayout();
    }

    this.registerOutsideZoneListeners();
    this.isDragDisabled.set(false);
  }

  private initLayout(): void {
    this.defaultLeftAreaWidth =
      (this.wrapper.nativeElement.clientWidth -
        this.separator.nativeElement.clientWidth) /
      2;

    this.renderer.setStyle(
      this.leftArea.nativeElement,
      'width',
      `${this.defaultLeftAreaWidth}px`
    );
  }

  private registerOutsideZoneListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      this.unlistenMouseMove = this.renderer.listen(
        this.document,
        'mousemove',
        (event: MouseEvent) => this.onDragMoved(event)
      );

      this.unlistenMouseUp = this.renderer.listen(
        this.document,
        'mouseup',
        () => this.onDragEnded()
      );
    });

    this.destroyRef.onDestroy(() => {
      this.unlistenMouseMove?.();
      this.unlistenMouseUp?.();
    });
  }

  onDragStarted(): void {
    this.isDragging = true;
  }

  onDragEnded(): void {
    this.isDragging = false;
  }

  onDragMoved(event: MouseEvent): void {
    if (!this.isDragging) {
      return;
    }

    const separatorRect = this.separator.nativeElement.getBoundingClientRect();
    const separatorMiddle = separatorRect.left + separatorRect.width / 2;

    const movingRightButCursorBeforeSeparator =
      event.movementX > 0 && event.clientX < separatorMiddle;
    const movingLeftButCursorAfterSeparator =
      event.movementX < 0 && event.clientX > separatorMiddle;

    if (
      movingRightButCursorBeforeSeparator ||
      movingLeftButCursorAfterSeparator
    ) {
      return;
    }

    const newWidth = Math.max(
      100,
      this.leftArea.nativeElement.getBoundingClientRect().width +
        event.movementX
    );

    this.renderer.setStyle(
      this.leftArea.nativeElement,
      'width',
      `${newWidth}px`
    );
  }

  onDragIconDoubleClick(): void {
    this.renderer.setStyle(
      this.leftArea.nativeElement,
      'width',
      `${this.defaultLeftAreaWidth}px`
    );
  }
}
